/**
 * createEngine — Public entry point for the engine.
 *
 * Orchestrates all subsystems:
 * 1. Validates container & WebGL2
 * 2. Creates AssetCache, AssetLoader, EngineStore, EventEmitter
 * 3. Mounts SceneManager (R3F Canvas)
 * 4. Loads the primary GLB model
 * 5. Wires the R3F scene tree
 * 6. Returns the Engine handle via buildEngineHandle()
 *
 * Method implementations live in buildEngineHandle.ts.
 *
 * @see docs/ims-engine-api/02-api-contract.md
 * @see docs/ims-engine-api/01-architecture.md
 */

import type {
  EngineConfig,
  Engine,
  LoadingProgress,
  ToneMappingMode,
  ShadowMode,
} from '../api/types';
import type { AssetVisibilityChangedEvent } from '../api/types/objectManager';
import { buildEngineHandle, type EngineInternalState } from './buildEngineHandle';
import { EnvironmentManager } from '../environment/EnvironmentManager';
import { EnvironmentComponent } from '../environment/EnvironmentComponent';
import { IBLManager } from '../environment/IBLManager';
import { PresetManager } from '../environment/PresetManager';
import { EventEmitter } from '../api/EventEmitter';
import { createEngineStore } from '../store/engineStore';
import { AssetCache } from '../core/AssetCache';
import { AssetLoader } from '../core/AssetLoader';
import { SceneManager } from '../core/SceneManager';
import { ModelComponent } from '../core/ModelComponent';
import { CameraManager } from '../core/CameraManager';
import { MaterialManager } from '../core/MaterialManager';
import { SunlightManager } from '../core/SunlightManager';
import { GroundShadowManager } from '../core/GroundShadowManager';
import { GroundShadowComponent } from '../core/GroundShadowComponent';
import type { CameraManagerRef } from '../core/CameraManager';
import type { GLTF } from 'three/addons/loaders/GLTFLoader.js';
import { Box3, Group, Raycaster, Vector2, Vector3 } from 'three';
import { createRef } from 'react';
import {
  DebugCubeComponent,
  MaterialManagerTicker,
  SunlightManagerComponent,
  AnimationManagerTicker,
  LightSignatureManagerTicker,
  WheelManagerTicker,
} from './EngineComponents';
import { LightSignatureManager } from '../core/LightSignatureManager';
import { WheelManager } from '../core/WheelManager';
import { PanoManager } from '../core/PanoManager';
import { PanoComponent } from '../core/PanoComponent';
import { DEFAULT_CAMERA, DEFAULT_AUTO_ROTATE } from '../core/CameraManager.constants';
import { ViewerSettingsManager } from '../core/ViewerSettingsManager';
import { PostFxPipeline } from '../core/PostFxPipeline';
import { AnimationManager } from '../core/AnimationManager';
import { DimensionsManager } from '../core/DimensionsManager';
import { DimensionsOverlay } from '../core/DimensionsOverlay';
import { ObjectManager } from '../core/ObjectManager';
import type { RevealObject } from '../core/RevealController';
import { ObjectComponent } from '../core/ObjectComponent';
import { PreloadManager } from '../core/PreloadManager';
import { applySceneAO } from '../materials/DownFacingAO';

// ---------------------------------------------------------------------------
// createEngine
// ---------------------------------------------------------------------------

/**
 * Create and initialise an engine instance.
 *
 * The returned `Engine` object is the only public handle to the engine.
 * Call `engine.dispose()` to tear everything down.
 */
export async function createEngine(config: EngineConfig): Promise<Engine> {
  // --- 1. Validate ---
  if (!config.container) {
    throw new Error('[IMS Engine] config.container is required.');
  }
  if (!config.modelUrl) {
    throw new Error('[IMS Engine] config.modelUrl is required.');
  }

  // WebGL2 check
  const testCanvas = document.createElement('canvas');
  const gl2 = testCanvas.getContext('webgl2');
  if (!gl2) {
    const err = {
      code: 'WEBGL_NOT_SUPPORTED' as const,
      message: 'WebGL2 is not supported by this browser.',
    };
    throw Object.assign(new Error(err.message), err);
  }

  // --- 2. Create subsystems ---
  const emitter = new EventEmitter();
  const store = createEngineStore();
  const cache = new AssetCache();
  const materialManager = new MaterialManager();
  const sunlightManager = new SunlightManager();
  if (config.shadowMapSize) {
    sunlightManager.keyLight.shadow.mapSize.set(config.shadowMapSize, config.shadowMapSize);
  }
  const groundShadowManager = new GroundShadowManager();
  const environmentManager = new EnvironmentManager();
  const iblManager = new IBLManager();
  const presetManager = new PresetManager(
    environmentManager,
    iblManager,
    sunlightManager,
    groundShadowManager,
  );

  // Viewer settings (PostFX + renderer control)
  const viewerSettingsManager = new ViewerSettingsManager(config.viewerSettings);

  // Animation system (initialised on model mount)
  const animationManager = new AnimationManager();

  // Dimensions overlay system
  const dimensionsManager = new DimensionsManager();

  // Object management system (multi-asset loading, reveal, swap)
  const objectManager = new ObjectManager((url, opts) =>
    loader
      .loadGLB(url, { phase: opts?.phase ?? 'lazy' })
      .then((gltf) => ({ scene: gltf.scene as unknown as RevealObject })),
  );
  objectManager.addVisibilityListener((e: AssetVisibilityChangedEvent) =>
    emitter.emit('assetVisibilityChanged', e),
  );
  objectManager.onSwapped = (e) => emitter.emit('assetSwapped', e);

  // Light signature system (animated headlight/taillight glass + emissive meshes)
  const lightSignatureManager = new LightSignatureManager();

  // Wheel animation system (continuous spin + steering)
  const wheelManager = new WheelManager();

  // Interior panorama system
  const panoManager = new PanoManager();
  panoManager.onChanged = (e) => emitter.emit('panoChanged', e);

  // Apply initial preset from config — orchestrates env + IBL + sunlight + shadow
  if (config.environment) {
    presetManager.apply(String(config.environment));
  }

  // Connect sunlight elevation changes to shadow length updates
  sunlightManager.onElevationChange = (elevation) => {
    groundShadowManager.applySunElevation(elevation);
  };

  const loader = new AssetLoader(cache, {
    dracoDecoderPath: config.dracoDecoderPath ?? '/assets/draco/',
    onAssetLoaded: (info) => {
      emitter.emit('assetLoaded', info);
    },
  });

  // Inject AssetLoader into MaterialManager so applyAtlasVariant() can load textures.
  materialManager.setAssetLoader(loader);

  // Register and start loading URL-based objects (e.g. aftermarket wheels)
  // in parallel with the body GLB. This way the wheel GLB is often already
  // cached by the time the user first clicks the rim selector.
  // Base-node objects (OEM wheels) are registered later in onMounted because
  // they need the model scene graph for traversal.
  if (config.objects?.length) {
    for (const objConfig of config.objects) {
      objectManager.register(objConfig);
    }
    for (const objConfig of config.objects) {
      objectManager.load(objConfig.id).catch((err) => {
        console.error(`[IMS] Failed to load object "${objConfig.id}" from ${objConfig.url}:`, err);
        emitter.emit('error', {
          code: 'ASSET_LOAD_FAILED' as const,
          message: `Failed to load object "${objConfig.id}": ${objConfig.url}`,
          details: err,
        });
      });
    }
  }

  // Preload manager — background asset pre-fetching with progress
  const preloadManager = new PreloadManager(loader, cache, {
    onProgress: (progress) => emitter.emit('preloadProgress', progress),
    onComplete: () => emitter.emit('preloadComplete'),
    hdriBasePath: config.hdriBasePath ?? '/assets/hdri',
  });

  // --- 3. Mount scene ---
  const sceneManager = new SceneManager({
    container: config.container,
    onContextLost: () => {
      emitter.emit('error', {
        code: 'CONTEXT_LOST',
        message: 'WebGL context was lost.',
      });
    },
    onError: (error) => {
      store.getState().setError(error);
      emitter.emit('error', error);
    },
  });

  // Mount initially empty
  sceneManager.mount();

  // --- 4. Start loading ---
  store.getState().setLoading(true);
  store.getState().setLoadingProgress(0, 'critical');

  // Emit initial loading progress
  emitter.emit('loadingProgress', { progress: 0, phase: 'critical' });

  let gltf: GLTF;
  try {
    gltf = await loader.loadGLB(config.modelUrl, {
      phase: 'critical',
      onProgress: (ratio) => {
        const progress = Math.round(ratio * 100);
        store.getState().setLoadingProgress(progress, 'critical');
        emitter.emit('loadingProgress', {
          progress,
          phase: 'critical',
        } satisfies LoadingProgress);
      },
    });
  } catch (err) {
    const engineError = {
      code: 'ASSET_LOAD_FAILED' as const,
      message: `Failed to load model: ${config.modelUrl}`,
      details: err,
    };
    store.getState().setError(engineError);
    emitter.emit('error', engineError);
    throw Object.assign(new Error(engineError.message), engineError);
  }

  // --- 5. Render model + camera ---
  store.getState().setLoadingProgress(100, 'critical');
  emitter.emit('loadingProgress', { progress: 100, phase: 'critical' });

  // CameraManager ref for runtime camera control
  const cameraRef = createRef<CameraManagerRef>();
  /** Plain mutable ref — DebugCubeComponent stores its setState here on mount. */
  // eslint-disable-next-line prefer-const
  let debugCubeSetVisible: { current: (v: boolean) => void } = { current: () => {} };

  // Pano mode camera + model helpers.
  // modelSceneRef is set in onMounted, then used imperatively by pano callbacks.
  const modelSceneRef = { current: null as Group | null };
  // envSceneRef wraps the entire EnvironmentComponent output (ground plane,
  // dome, road markings, spotlight panels, etc.). Hidden during pano mode so
  // no environment geometry leaks through the panorama sphere.
  const envSceneRef = { current: null as Group | null };

  const mergedCamera = { ...DEFAULT_CAMERA, ...(config.camera ?? {}) };
  const mergedAutoRot = { ...DEFAULT_AUTO_ROTATE, ...(config.autoRotate ?? {}) };

  // Saved pre-pano tone mapping — restored when exiting pano mode.
  const prePanoPostFx = {
    toneMapping: 'aces-filmic' as ToneMappingMode,
  };
  let prePanoCameraFov = 75; // Three.js PerspectiveCamera default
  let prePanoShadowMode: ShadowMode = 'contact';
  let prePanoDimensionsVisible = false;
  let panoActive = false;

  const onEnterPano = () => {
    // ── Dimensions ── hide FIRST so that onToggled restores SSAO to its
    // natural state before we snapshot below.
    prePanoDimensionsVisible = dimensionsManager.getState().visible;
    if (prePanoDimensionsVisible) dimensionsManager.toggle(false);

    // ── PostFX ── Only switch tone mapping to linear.
    //
    // The EffectComposer's OutputPass applies gl.toneMapping (e.g. ACES
    // Filmic) to the entire composited frame — including the pano sphere.
    // ACES on an already-correctly-graded photo produces an over-contrasted,
    // shifted result. Switching to 'linear' makes OutputPass a pure sRGB
    // round-trip (decode linear → encode sRGB) with no tone curve applied.
    //
    // SSAO, Bloom, Sharpen, and Antialiasing are deliberately kept ACTIVE
    // so their shader programs stay compiled. Toggling them off/on caused
    // a GPU spike (5+ shader recompilations in one frame) when exiting
    // pano mode, crashing iOS Safari. Keeping them on has minimal visual
    // impact: SSAO has no depth edges on a smooth sphere, Bloom only
    // affects extreme highlights, Sharpen is subtle on photo textures.
    prePanoPostFx.toneMapping = viewerSettingsManager.getState().toneMapping;
    viewerSettingsManager.apply({
      toneMapping: 'linear',
    });

    // ── Shadow ── hide ground shadow (baked plane / contact shadows poke through the pano sphere)
    prePanoShadowMode = groundShadowManager.mode;
    groundShadowManager.apply('off');

    // Save current FOV before overriding it with the pano preset's 90°
    const cam = (cameraRef.current?.controls as unknown as { camera?: { fov?: number } } | null)
      ?.camera;
    if (cam?.fov !== undefined) prePanoCameraFov = cam.fov;

    // Apply the pano preset (wide FOV + full polar/azimuth + locked distance)
    cameraRef.current?.setAutoRotate({ enabled: false });
    cameraRef.current?.setCameraPreset('pano');

    // Apply model-specific pano FOV override (after preset so it wins).
    const panoFov = config.panoFov;
    if (panoFov !== undefined) {
      const aspect = config.container.clientWidth / (config.container.clientHeight || 1);
      const isLandscape = aspect > 1;
      const fov =
        typeof panoFov === 'number' ? panoFov : isLandscape ? panoFov.landscape : panoFov.portrait;
      cameraRef.current?.setCamera({ fov });
    }
    // Move camera to sphere centre (no transition — behind the black curtain)
    cameraRef.current?.controls?.setLookAt(-0.1, 0, 0.001, 0, 0, 0, false);
    // Hide the vehicle model + all body-scope objects (aftermarket rims etc.).
    // clonedScene's parent is the inner offset group that contains BOTH the body
    // GLB primitive AND the body-scope ObjectComponent children — hiding the
    // parent group covers them all in one operation.
    const modelGroup = modelSceneRef.current?.parent ?? modelSceneRef.current;
    if (modelGroup) modelGroup.visible = false;
    // Hide all environment geometry (ground plane, road markings, dome,
    // spotlight panels, reflection plane, etc.) so nothing leaks through
    // the panorama sphere.
    if (envSceneRef.current) envSceneRef.current.visible = false;

    panoActive = true;
  };

  const onExitPano = () => {
    panoActive = false;

    // ── PostFX ── restore only tone mapping (other effects stayed active)
    viewerSettingsManager.apply({
      toneMapping: prePanoPostFx.toneMapping,
    });

    // ── Shadow ── restore previous shadow mode
    groundShadowManager.apply(prePanoShadowMode);

    // ── Dimensions ── restore overlay if it was visible before entering pano
    if (prePanoDimensionsVisible) dimensionsManager.toggle(true);

    // Restore original camera constraints, FOV, and position
    cameraRef.current?.setCamera({ ...mergedCamera, fov: prePanoCameraFov });
    cameraRef.current?.setAutoRotate({ enabled: mergedAutoRot.enabled });
    cameraRef.current?.reset();
    // Restore vehicle model + body-scope objects visibility
    const modelGroup = modelSceneRef.current?.parent ?? modelSceneRef.current;
    if (modelGroup) modelGroup.visible = true;
    // Restore environment geometry
    if (envSceneRef.current) envSceneRef.current.visible = true;
  };

  // ── Pano FOV resize listener ──
  // When panoFov has portrait/landscape values and the device rotates while
  // in pano mode, we must re-apply the correct FOV for the new orientation.
  // onEnterPano only runs once — this observer handles subsequent changes.
  const panoFov = config.panoFov;
  if (panoFov !== undefined && typeof panoFov !== 'number') {
    let prevIsLandscape: boolean | null = null;
    const panoResizeObserver = new ResizeObserver(() => {
      if (!panoActive) return;
      const aspect = config.container.clientWidth / (config.container.clientHeight || 1);
      const isLandscape = aspect > 1;
      if (isLandscape === prevIsLandscape) return; // same orientation — skip
      prevIsLandscape = isLandscape;
      const fov = isLandscape ? panoFov.landscape : panoFov.portrait;
      cameraRef.current?.setCamera({ fov });
    });
    panoResizeObserver.observe(config.container);
  }

  // Material targets come from the frontend config — the engine has no hardcoded mesh names.
  const materialTargets = config.materialTargets ?? [];
  const glassMaterialTargets = config.glassMaterialTargets ?? [];
  const glassType = config.glassType ?? 'toned-mid';
  const atlasMaterials = config.atlasMaterials ?? [];

  sceneManager.update(
    <>
      <MaterialManagerTicker manager={materialManager} />
      <AnimationManagerTicker manager={animationManager} />
      <LightSignatureManagerTicker manager={lightSignatureManager} />
      <WheelManagerTicker manager={wheelManager} />
      <PanoComponent manager={panoManager} onEnterPano={onEnterPano} onExitPano={onExitPano} />
      <SunlightManagerComponent manager={sunlightManager} />
      <GroundShadowComponent
        manager={groundShadowManager}
        viewerSettingsManager={viewerSettingsManager}
      />
      <DebugCubeComponent setVisibleRef={debugCubeSetVisible} />
      <EnvironmentComponent
        manager={environmentManager}
        iblManager={iblManager}
        hdriBasePath={config.hdriBasePath ?? '/assets/hdri'}
        assetLoader={loader}
        viewerSettingsManager={viewerSettingsManager}
        groupRef={envSceneRef}
      />
      <PostFxPipeline manager={viewerSettingsManager} />
      <DimensionsOverlay manager={dimensionsManager} />
      <ObjectComponent
        objectManager={objectManager}
        groundSinkY={config.groundSinkY ?? 0.02}
        scope="world"
      />
      <ModelComponent
        gltf={gltf}
        autoCenter
        groundSinkY={config.groundSinkY ?? 0.02}
        onMounted={(clonedScene) => {
          // Use clonedScene — the actual rendered object. gltf.scene is the
          // cached original; MaterialManager must reference the rendered meshes.
          materialManager.init(clonedScene, materialTargets);
          if (glassMaterialTargets.length > 0) {
            materialManager.init(clonedScene, glassMaterialTargets, 'glass', glassType);
          }
          materialManager.initAtlas(clonedScene, atlasMaterials);

          // Auto-fit shadow frustum from model AABB (eliminates pixelated /
          // clipped shadows without any manual shadow camera configuration).
          const aabb = new Box3().setFromObject(clonedScene);
          sunlightManager.fitShadowFrustum(aabb);
          groundShadowManager.setModelAabb(aabb);
          // Cache AABB for cheap Ray-vs-Box click test (dimensions toggle)
          modelAabb = aabb;

          // Initialise animation mixer from the rendered scene + GLTF clips
          animationManager.init(clonedScene, gltf.animations);

          // Auto-switch contact shadow to live (Infinity frames) while any
          // animation is running, then back to static (1 frame) when idle.
          // This makes door/hood animations cast correct real-time shadows
          // without burning GPU every frame during normal use.
          let _activeAnimationCount = 0;
          animationManager.onStarted = (e) => {
            emitter.emit('animationStarted', e);
            _activeAnimationCount++;
            if (_activeAnimationCount === 1) {
              groundShadowManager.apply({ frames: Infinity });
            }
          };
          animationManager.onFinished = (e) => {
            emitter.emit('animationFinished', e);
            _activeAnimationCount = Math.max(0, _activeAnimationCount - 1);
            if (_activeAnimationCount === 0) {
              groundShadowManager.apply({ frames: 1 });
            }
          };

          // Initialise dimensions overlay from AABB + optional config
          dimensionsManager.init(
            {
              min: { x: aabb.min.x, y: aabb.min.y, z: aabb.min.z },
              max: { x: aabb.max.x, y: aabb.max.y, z: aabb.max.z },
            },
            config.dimensions,
          );
          dimensionsManager.onToggled = (e) => {
            // SSAO causes black edges on the dimension overlay planes.
            // Disable SSAO while dimensions are visible and restore afterwards.
            if (e.visible) {
              _ssaoEnabledBeforeDimensions = viewerSettingsManager.getState().ssao.enabled;
              if (_ssaoEnabledBeforeDimensions) {
                viewerSettingsManager.apply({ ssao: { enabled: false } });
              }
            } else {
              if (_ssaoEnabledBeforeDimensions) {
                viewerSettingsManager.apply({ ssao: { enabled: true } });
              }
              _ssaoEnabledBeforeDimensions = false;
            }
            emitter.emit('dimensionsToggled', e);
          };

          // Global shader effect: down-facing AO on opaque materials only.
          // Transparent materials (glass, headlight lenses) are skipped.
          applySceneAO(clonedScene, { strength: 0.8, color: [0, 0, 0] });

          // Expose clonedScene for pano model-visibility toggle
          modelSceneRef.current = clonedScene as unknown as Group;

          // Register base nodes from EngineConfig BEFORE setModelScene so the
          // traversal finds and wires them in one pass.
          if (config.baseNodes?.length) {
            for (const bn of config.baseNodes) {
              objectManager.registerBaseNode(bn);
            }
          }

          // Connect registered base nodes to the rendered model scene graph
          objectManager.setModelScene(clonedScene as unknown as RevealObject);

          // Compute group anchor centers from the ORIGINAL (unmounted) gltf.scene
          // so that external replacement objects (e.g. aftermarket rims) can be
          // aligned to the same position as the OEM base node.
          // Using gltf.scene (not clonedScene) guarantees pristine matrices
          // without any R3F parent transforms.
          if (config.baseNodes?.length) {
            gltf.scene.updateWorldMatrix(false, true);
            for (const bn of config.baseNodes) {
              let foundNode: import('three').Object3D | null = null;
              gltf.scene.traverse((child: import('three').Object3D) => {
                if (child.name === bn.nodeName) foundNode = child;
              });
              if (foundNode) {
                const box = new Box3().setFromObject(foundNode);
                if (!box.isEmpty()) {
                  const center = box.getCenter(new Vector3());
                  objectManager.setGroupAnchor(bn.group, { x: center.x, y: center.y, z: center.z });
                }
              }
            }
          }

          store.getState().setReady(true);
          emitter.emit('sceneLoaded');

          // Initialise wheel animation system (optional feature)
          if (config.wheel) {
            wheelManager.init(clonedScene, config.wheel);
            wheelManager.onChanged = (e) => emitter.emit('wheelChanged', e);
          }

          // Initialise light signature (optional feature)
          // Fire-and-forget: sync traversal runs before first await, so the
          // scene state is consistent on the next frame even if a texture
          // URL load is still in-flight.
          if (config.lightSignature) {
            lightSignatureManager.init(clonedScene, config.lightSignature, loader).catch((err) => {
              emitter.emit('error', {
                code: 'ASSET_LOAD_FAILED' as const,
                message: `Light signature init failed: ${err}`,
                details: err,
              });
            });
            lightSignatureManager.onChanged = (e) => emitter.emit('lightSignatureChanged', e);
          }

          // URL-based objects were already registered + loaded earlier
          // (in parallel with the body GLB). Nothing to do here.
          // Base-node objects are handled above via registerBaseNode + setModelScene.
        }}
      >
        {/* Body-scope: parentRelative objects (rims, accessories) rendered as
            children of the model group — automatically inherit its centering
            transform, so no manual offset math is needed. */}
        <ObjectComponent objectManager={objectManager} groundSinkY={0} scope="body" />
      </ModelComponent>
      <CameraManager
        ref={cameraRef}
        config={config.camera}
        autoRotate={config.autoRotate}
        onAutoRotateChange={(rotating) => {
          store.getState().setAutoRotating(rotating);
        }}
      />
    </>,
  );

  // --- 5b. DOM-based click for dimensions toggle ---
  // R3F's event system is disabled (perf: raycasting 100-500 meshes per
  // pointermove caused 400ms+ Long Tasks). Instead, a cheap Ray-vs-AABB
  // test fires only on click — near-zero overhead (0.01ms vs 85ms).
  const domRaycaster = new Raycaster();
  const domMouse = new Vector2();
  /** AABB of the model — set once in onMounted, used for cheap hit test. */
  let modelAabb: Box3 | null = null;
  /** Whether SSAO was enabled before dimensions were opened. */
  let _ssaoEnabledBeforeDimensions = false;

  // Drag detection: record pointer position on down so we can ignore
  // clicks that were actually orbit-control drags.
  const DRAG_THRESHOLD_PX = 5;
  let _pointerDownX = 0;
  let _pointerDownY = 0;
  const handlePointerDown = (e: PointerEvent) => {
    _pointerDownX = e.clientX;
    _pointerDownY = e.clientY;
  };
  const handleContainerClick = (e: MouseEvent) => {
    // Skip if the pointer moved too much — user was dragging, not clicking.
    const dx = e.clientX - _pointerDownX;
    const dy = e.clientY - _pointerDownY;
    if (Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD_PX) return;

    const camera = sceneManager.camera;
    if (!camera || engineState.disposed || !modelAabb || panoActive) return;
    const rect = config.container.getBoundingClientRect();
    domMouse.set(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
    domRaycaster.setFromCamera(domMouse, camera);
    // Cheap Ray-vs-AABB test (no per-triangle intersection)
    if (domRaycaster.ray.intersectsBox(modelAabb)) {
      dimensionsManager.toggle();
    }
  };
  config.container.addEventListener('pointerdown', handlePointerDown);
  config.container.addEventListener('click', handleContainerClick);

  // --- 6. Build Engine handle ---
  const engineState: EngineInternalState = { disposed: false, dimensionsActivated: false };

  const engine = buildEngineHandle({
    state: engineState,
    container: config.container,
    emitter,
    store,
    cache,
    loader,
    sceneManager,
    materialManager,
    sunlightManager,
    groundShadowManager,
    iblManager,
    environmentManager,
    presetManager,
    viewerSettingsManager,
    animationManager,
    dimensionsManager,
    objectManager,
    preloadManager,
    lightSignatureManager,
    wheelManager,
    panoManager,
    cameraRef,
    debugCubeSetVisible,
    handlePointerDown,
    handleContainerClick,
  });

  return engine;
}
